import { useEffect, useState } from 'react';
import { throttle } from 'lodash-es';
import { Popup, useMap } from 'react-map-gl';
import { useAtomValue } from 'jotai';
import { Loading } from '@carbon/react';
import type { MapMouseEvent, MapboxGeoJSONFeature } from 'mapbox-gl';
import { Link } from 'components/link';
// The shared `.popup` class, as the documents, wells and footprint popups use.
import { TypeCurveLinksTable, mapStyles } from 'components/map';
import { allDatasetAtoms } from '../atoms';
import { areasConfig, formatAcres } from '../datasets/areas-utils';
import { documentsConfig } from '../datasets/documents.config';
import { wellsConfig } from '../datasets/wells.config';
import { subsurfaceMapsConfig } from '../datasets/subsurface-maps.config';
import { CELLS_LAYER_ID } from '../subsurface-maps/utils';
import { queryFeaturesNear } from './feature-hit-test';
import { docLayerId } from './tile-layer-utils';
import { PopupCard } from './popup-card';
import { useTypeCurveLinks } from './hooks';
import {
  areaAcres,
  areaFrom,
  areaHref,
  cardPlacement,
  type AreaFeature,
  type CardPlacement,
} from './area-popup-utils';
import styles from './area-popup.module.scss';

const datasetAtoms = allDatasetAtoms.areas;

// The area fill is the hover and click target; the outline is too thin to hit.
const FILL_LAYER_ID = areasConfig.tileLayers?.[0]
  ? docLayerId(areasConfig.tileLayers[0], 0)
  : '';

// The well and document dots the wells and documents popups claim within a few
// pixels of the pointer (their own hit-test). A point near one is theirs, so an area
// under it opens nothing rather than a second popup.
const DOT_LAYER_IDS = [
  ...(wellsConfig.tileLayers ?? []).flatMap((layer, idx) =>
    layer.hitTarget ? [docLayerId(layer, idx)] : []
  ),
  ...(documentsConfig.tileLayers ?? []).map((layer, idx) =>
    docLayerId(layer, idx)
  ),
];

// Petrotech keeps every point it had before Areas were on the map: where a footprint
// or the shown map's cells lie under the pointer, its own popup answers.
const PETROTECH_LAYER_IDS = [
  ...(subsurfaceMapsConfig.tileLayers ?? []).flatMap((layer, idx) =>
    layer.layerType === 'fill' ? [docLayerId(layer, idx)] : []
  ),
  CELLS_LAYER_ID,
];

// Payload keys that move with the map or the page, not the filters: a card stays
// open across them, and closes when a filter changes what the map shows.
const NON_FILTER_KEYS = new Set([
  'bounding_box',
  'polygon_geojson',
  'page',
  'pageSize',
  'sortAttribute',
  'sortDirection',
]);

/**
 * The filters in a search payload, as a key that changes only when they do.
 * @param {Record<string, unknown> | undefined} payload The search payload.
 * @return {string} The key.
 */
const filterKey = (payload: Record<string, unknown> | undefined): string =>
  JSON.stringify(
    Object.entries(payload ?? {})
      .filter(([key]) => !NON_FILTER_KEYS.has(key))
      .sort(([a], [b]) => a.localeCompare(b))
  );

// The documents, wells and footprint popups throttle their hover at 50 ms; the
// pointer is claimed just past one window, after DocumentHoverPopup's clear.
const HOVER_THROTTLE_MS = 50;
const CURSOR_CLAIM_DELAY_MS = HOVER_THROTTLE_MS + 10;

/** Where a card sits, and the area it shows. */
interface Placed {
  area: AreaFeature;
  lngLat: [number, number];
}

/** A pinned card: also which way it opens and how big it may be, to stay in the map. */
interface Pinned extends Placed {
  placement: CardPlacement;
}

/**
 * A TypeCurve area's Associated Links: its type curves, as the type curve area
 * popup lists them.
 * @param {{ areaName: string; tableHeight: number }} props The area's name, and how
 *   tall its table may be to keep the card inside the map.
 * @return {JSX.Element} The section.
 */
function AssociatedLinks({
  areaName,
  tableHeight,
}: {
  areaName: string;
  tableHeight: number;
}): JSX.Element {
  const { data, isLoading, isError } = useTypeCurveLinks(areaName);
  let body: JSX.Element;
  if (isLoading) body = <Loading small withOverlay={false} />;
  else if (isError) body = <span>Could not load the type curves.</span>;
  else if (!data?.length)
    body = <span>No type curves found for this area.</span>;
  else
    body = (
      <TypeCurveLinksTable typeCurves={data} height={tableHeight} fitWidth />
    );
  return (
    <div className={styles.links}>
      <span className={styles.linksLabel}>Associated Links</span>
      {body}
    </div>
  );
}

/**
 * Areas on the Map Query map (UAT): hovering an area shows its name; clicking it
 * pins a card with its details, and for a TypeCurve area its Associated Links. A
 * point near a well or document dot, or over a Petrotech footprint or cells, is left
 * to that feature's own popup. Hidden while Areas is off the map; a pinned card
 * closes when Areas goes off the map or a filter changes what it shows.
 * @param {{ disabled?: boolean }} props Off while measuring or drawing an AOI.
 * @return {JSX.Element | null} The tooltip and the pinned card.
 */
export function AreaPopup({
  disabled = false,
}: {
  disabled?: boolean;
}): JSX.Element | null {
  const { current: mapRef } = useMap();
  const visible = useAtomValue(datasetAtoms.onMapAtom);
  const search = useAtomValue(datasetAtoms.searchAtom);
  const [hovered, setHovered] = useState<Placed | null>(null);
  const [pinned, setPinned] = useState<Pinned | null>(null);
  const active = !disabled && visible;
  // Going off the map, or a filter change, drops the cards rather than leaving one
  // over an area no longer drawn (reset during render, so no effect re-renders).
  // Measuring or drawing only hides them, as it does the wells popup.
  const resetKey = `${visible}|${filterKey(search.currentPayload)}`;
  const [shownFor, setShownFor] = useState(resetKey);
  if (shownFor !== resetKey) {
    setShownFor(resetKey);
    setHovered(null);
    setPinned(null);
  }

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !active || !FILL_LAYER_ID) return undefined;

    /**
     * The area under the point, unless a well or document dot is near it or a
     * Petrotech footprint or cells lie under it. Lines and outlines over the area
     * don't take it: they have no popup of their own. The area fill is queried first,
     * so a point off every area costs one layer's hit-test.
     * @param {MapMouseEvent} event The pointer event.
     * @return {MapboxGeoJSONFeature | null} The area's fill feature.
     */
    const areaAt = (event: MapMouseEvent): MapboxGeoJSONFeature | null => {
      if (!map.getLayer(FILL_LAYER_ID)) return null;
      const area = map.queryRenderedFeatures(event.point, {
        layers: [FILL_LAYER_ID],
      })[0];
      if (!area) return null;
      if (queryFeaturesNear(map, event.point, DOT_LAYER_IDS).length) {
        return null;
      }
      const petrotech = PETROTECH_LAYER_IDS.filter((id) => map.getLayer(id));
      if (
        petrotech.length &&
        map.queryRenderedFeatures(event.point, { layers: petrotech }).length
      ) {
        return null;
      }
      return area;
    };

    let overArea = false;
    let cursorTimer: ReturnType<typeof setTimeout> | undefined;
    /**
     * Claims the pointer cursor once DocumentHoverPopup's clear has run. A claim
     * already pending is left to fire, so a moving pointer still gets it (re-arming
     * on every move would push it past each new throttle window).
     */
    const claimCursor = () => {
      if (cursorTimer) return;
      cursorTimer = setTimeout(() => {
        cursorTimer = undefined;
        if (overArea) map.getCanvas().style.cursor = 'pointer';
      }, CURSOR_CLAIM_DELAY_MS);
    };

    const onMove = throttle((event: MapMouseEvent) => {
      const feature = areaAt(event);
      overArea = !!feature;
      if (!feature) {
        setHovered(null);
        return;
      }
      claimCursor();
      const lngLat: [number, number] = [event.lngLat.lng, event.lngLat.lat];
      const area = areaFrom(feature);
      // Same area: only the anchor moves.
      setHovered((current) =>
        current?.area.id === area.id ? { ...current, lngLat } : { area, lngLat }
      );
    }, HOVER_THROTTLE_MS);
    /**
     * Pins the clicked area's card; a click off every area closes it.
     * @param {MapMouseEvent} event The click.
     */
    const onClick = (event: MapMouseEvent) => {
      const feature = areaAt(event);
      if (!feature) {
        setPinned(null);
        return;
      }
      const { width, height } = map.getContainer().getBoundingClientRect();
      setPinned({
        area: areaFrom(feature),
        lngLat: [event.lngLat.lng, event.lngLat.lat],
        placement: cardPlacement(event.point, { width, height }),
      });
    };
    /** Drops the tooltip as the pointer leaves the map. */
    const onLeave = () => {
      onMove.cancel();
      overArea = false;
      setHovered(null);
    };

    map.on('mousemove', onMove);
    map.on('click', onClick);
    map.on('mouseout', onLeave);
    return () => {
      onMove.cancel();
      clearTimeout(cursorTimer);
      map.off('mousemove', onMove);
      map.off('click', onClick);
      map.off('mouseout', onLeave);
    };
  }, [mapRef, active]);

  if (!active) return null;
  const showTooltip = !!hovered && hovered.area.id !== pinned?.area.id;
  if (!showTooltip && !pinned) return null;

  return (
    <>
      {showTooltip && (
        <Popup
          longitude={hovered.lngLat[0]}
          latitude={hovered.lngLat[1]}
          closeButton={false}
          closeOnClick={false}
          className={mapStyles.popup}
          offset={12}
        >
          <span className={styles.tooltipName}>{hovered.area.name}</span>
        </Popup>
      )}
      {pinned && (
        <Popup
          longitude={pinned.lngLat[0]}
          latitude={pinned.lngLat[1]}
          closeButton
          closeOnClick={false}
          onClose={() => setPinned(null)}
          className={mapStyles.popup}
          anchor={pinned.placement.anchor}
          maxWidth={`${pinned.placement.maxWidth}px`}
        >
          <PopupCard
            title={
              <Link
                to={areaHref(pinned.area.id, search.data?.results)}
                openInNewTab
              >
                {pinned.area.name}
              </Link>
            }
            columns={[
              [
                ['Area Type', pinned.area.category ?? '—'],
                ['Status', pinned.area.status ?? '—'],
                ['Shared', pinned.area.shared ? 'Shared' : 'Private'],
                ['Source', pinned.area.dataSource ?? '—'],
              ],
              [
                [
                  'Gross Acres',
                  formatAcres(areaAcres(pinned.area, search.data?.results)) ||
                    '—',
                ],
                ['Created By', pinned.area.createdBy ?? '—'],
              ],
            ]}
          >
            {pinned.area.category === 'TypeCurve' && (
              <AssociatedLinks
                areaName={pinned.area.name}
                tableHeight={pinned.placement.tableHeight}
              />
            )}
          </PopupCard>
        </Popup>
      )}
    </>
  );
}
